import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveLineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, SupplierPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ApStatusChip, fmtDate, getErrorMessage, unwrap } from './ApShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'PARTIALLY_PAID', 'PAID', 'VOID'];

const supplierLabel = (inv, look) => {
  if (inv?.supplier) return inv.supplier.name || `Supplier ${inv.supplier_id}`;
  return look.supplierLabel(inv?.supplier_id, `Supplier ${inv?.supplier_id ?? ''}`);
};

const emptyItem = () => ({ item_name: '', quantity: 1, unit_price: '', expense_account_id: '' });
const emptyForm = () => ({
  supplier_id: '', invoice_date: new Date().toISOString().slice(0, 10), due_date: '',
  items: [emptyItem()],
});

// Supplier bills: one AP invoice per supplier, drawn against an expense
// account per line - the mirror image of receivables/InvoicesTab.js (see
// that file for the verified AR contract this follows; there's no
// AccountingClient.php equivalent for AP). DRAFT -> APPROVED -> POSTED;
// approve/void run as checker, create/post as maker.
const PayableInvoicesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.AP, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.AP, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.AP, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const setItem = (index, key, value) => {
    setValues((p) => ({ ...p, items: p.items.map((it, i) => (i === index ? { ...it, [key]: value } : it)) }));
  };
  const addItem = () => setValues((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (index) => setValues((p) => ({ ...p, items: p.items.filter((_, i) => i !== index) }));

  const itemsTotal = values.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submitCreate = async () => {
    if (!values.supplier_id) {
      showSnackbar('Supplier is required', 'error');
      return;
    }
    const items = values.items
      .filter((it) => it.item_name || it.unit_price)
      .map((it) => ({
        item_name: it.item_name.trim(),
        quantity: Number(it.quantity) || 1,
        unit_price: Number(it.unit_price),
        expense_account_id: Number(it.expense_account_id),
      }));
    if (items.length < 1) {
      showSnackbar('At least one bill line is required', 'error');
      return;
    }
    if (items.some((it) => !it.item_name || !(it.unit_price > 0) || !it.expense_account_id)) {
      showSnackbar('Each line needs a description, expense account and unit price greater than zero', 'error');
      return;
    }
    const payload = {
      supplier_id: Number(values.supplier_id),
      invoice_date: values.invoice_date,
      items,
    };
    if (values.due_date) payload.due_date = values.due_date;
    try {
      setSaving(true);
      await ledgerAPI.createPayableInvoice(payload);
      showSnackbar('Bill created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this bill?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Bill ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getPayableInvoice(row.invoice_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch bill'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidPayableInvoice(voidDlg.item.invoice_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Bill voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailItems = Array.isArray(detail?.items) ? detail.items : [];

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.AP}
        title="Supplier Bills"
        subtitle="AP invoices billed by suppliers - one per bill, drawn against an expense account per line"
        addLabel="New Bill"
        idKey="invoice_id"
        searchPlaceholder="Search bill #, supplier..."
        emptyMessage="No bills found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getPayableInvoices(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'supplier_id', label: 'Supplier', picker: SupplierPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Invoice date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'total' },
        ]}
        columns={[
          { label: 'Bill #', render: (i) => <strong>{i.invoice_number || `BILL-${i.invoice_id}`}</strong> },
          { label: 'Supplier', render: (i) => supplierLabel(i, look) },
          { label: 'Invoice Date', render: (i) => fmtDate(i.invoice_date) },
          { label: 'Due Date', render: (i) => fmtDate(i.due_date), hideOnMobile: true },
          { label: 'Total', render: (i) => formatAccountingAmount(i.total_amount ?? i.grand_total), align: 'right' },
          { label: 'Balance Due', render: (i) => formatAccountingAmount(i.balance_due), align: 'right' },
          { label: 'Status', render: (i) => <ApStatusChip status={i.status} /> },
        ]}
        rowActions={(i) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(i) }];
          if (i.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approvePayableInvoice(i.invoice_id)) });
          }
          if (i.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postPayableInvoice(i.invoice_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED', 'PARTIALLY_PAID'].includes(i.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: i, void_reason: '' }) });
          }
          if (i.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete bill',
                message: `Delete DRAFT bill ${i.invoice_number || `BILL-${i.invoice_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deletePayableInvoice(i.invoice_id);
                    showSnackbar('Bill deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Supplier Bill</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <SupplierPicker label="Supplier" value={values.supplier_id} onChange={(id) => setValues((p) => ({ ...p, supplier_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Invoice Date" value={values.invoice_date}
                onChange={(e) => setValues((p) => ({ ...p, invoice_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Due Date (optional)" value={values.due_date}
                onChange={(e) => setValues((p) => ({ ...p, due_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Bill lines</Typography>
          {values.items.map((it, i) => (
            <Stack key={i} direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <TextField
                size="small" label="Description" value={it.item_name}
                onChange={(e) => setItem(i, 'item_name', e.target.value)}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                size="small" type="number" label="Qty" value={it.quantity}
                onChange={(e) => setItem(i, 'quantity', e.target.value)}
                inputProps={{ min: 1, step: 1 }} sx={{ width: 90 }}
              />
              <TextField
                size="small" type="number" label="Unit Price" value={it.unit_price}
                onChange={(e) => setItem(i, 'unit_price', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} sx={{ width: 140 }}
              />
              <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
                <AccountPicker label="Expense account" value={it.expense_account_id} onChange={(id) => setItem(i, 'expense_account_id', id)} />
              </Box>
              <IconButton size="small" color="error" title="Remove line" onClick={() => removeItem(i)} disabled={values.items.length <= 1}>
                <RemoveLineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Add line</Button>
          <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
            Total: {formatAccountingAmount(itemsTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Bill {detail?.invoice_number || (detail?.invoice_id ? `BILL-${detail.invoice_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {supplierLabel(detail, look)} - {fmtDate(detail.invoice_date)}
                {detail.due_date ? ` - due ${fmtDate(detail.due_date)}` : ''}
              </Typography>
              <DataTable
                columns={['Description', 'Qty', 'Unit Price', 'Line Total']}
                data={detailItems}
                loading={false}
                emptyMessage="No lines."
                renderRow={(it, i) => (
                  <TableRow key={it.item_id ?? i}>
                    <TableCell>{it.item_name}</TableCell>
                    <TableCell>{it.quantity}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(it.unit_price)}</TableCell>
                    <TableCell align="right">{formatAccountingAmount((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Total: <strong>{formatAccountingAmount(detail.total_amount ?? detail.grand_total)}</strong></Typography>
                <Typography variant="body2">Balance Due: <strong>{formatAccountingAmount(detail.balance_due)}</strong></Typography>
                <ApStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void bill ${voidDlg.item?.invoice_number || (voidDlg.item ? `BILL-${voidDlg.item.invoice_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default PayableInvoicesTab;
